import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import APIAgent from '../../../agents/apiAgent';
import asArray from '../../../utils/asArray';
import { getNotifDateGroup } from '../utils';

export const OFFER_NOTIFICATIONS_URL = '/api/offer-notifications';
const PAGE_SIZE = 50;

/**
 * A fetched page with the optimistic reads it may predate. A mark applies
 * while its POST is in flight, or when it was made after the GET started
 * (`mark.version > fetchVersion`): either way the server's row can't show it yet.
 */
export function applyReadMarks(rows, serverUnread, marks, allMark, fetchVersion) {
  const pending = (mark) => Boolean(mark) && (!mark.settled || mark.version > fetchVersion);
  const allPending = pending(allMark);
  let flipped = 0;
  const merged = rows.map((n) => {
    if (n.is_read) return n;
    const covered = pending(marks.get(n.id)) || (allPending && allMark.ids.has(n.id));
    if (!covered) return n;
    flipped += 1;
    return { ...n, is_read: true };
  });
  let unread = serverUnread;
  if (typeof unread === 'number') {
    // After "mark all read", only rows that arrived since are unread.
    unread = allPending
      ? merged.filter((n) => !n.is_read).length
      : Math.max(0, unread - flipped);
  }
  return { rows: merged, unread };
}

/**
 * The signed-in user's deal notifications (`/api/offer-notifications`).
 *
 * Each item carries `notification_type`, `copy_key`, `copy_params`, `link`,
 * `viewer_role`, `is_read` and `created_at` (the sent time). Marking read is
 * optimistic and persisted; a failed POST reverts by refetching.
 *
 * A poll can be in flight when a row is marked read, or start before the
 * mark's POST lands. Each mark records the version it was made at; a fetched
 * page keeps every mark it may predate (`applyReadMarks`), so a poll never
 * shows a read row as unread again.
 *
 * @param {{userId: (number|string|null), pollMs?: number}} options
 */
export function useOfferNotifications({ userId, pollMs = 60000 }) {
  const [notifications, setNotifications] = useState([]);
  const [serverUnread, setServerUnread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  // Bumped by every optimistic read; a fetch remembers the version it started at.
  const versionRef = useRef(0);
  const readMarksRef = useRef(new Map()); // id -> { version, settled }
  const allReadMarkRef = useRef(null); // { version, settled, ids }
  const fetchSeqRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    const seq = ++fetchSeqRef.current;
    const fetchVersion = versionRef.current;
    setLoading(true);
    try {
      const data = await APIAgent.request(`${OFFER_NOTIFICATIONS_URL}?limit=${PAGE_SIZE}`, 'GET');
      // A newer fetch has started: its page is the one to show.
      if (seq !== fetchSeqRef.current) return;
      const { rows, unread } = applyReadMarks(
        asArray(data?.notifications ?? data),
        typeof data?.unread_count === 'number' ? data.unread_count : null,
        readMarksRef.current,
        allReadMarkRef.current,
        fetchVersion,
      );
      setNotifications(rows);
      setServerUnread(unread);
      setError(null);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError('Error fetching notifications: ' + err.message);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!pollMs || !userId) return undefined;
    timerRef.current = setInterval(fetchNotifications, pollMs);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchNotifications, pollMs, userId]);

  const markAllRead = useCallback(async () => {
    const mark = {
      version: ++versionRef.current,
      settled: false,
      ids: new Set(notificationsRef.current.map((n) => n.id)),
    };
    allReadMarkRef.current = mark;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setServerUnread(0);
    try {
      await APIAgent.request(`${OFFER_NOTIFICATIONS_URL}/read-all`, 'POST');
      mark.settled = true;
    } catch (err) {
      if (allReadMarkRef.current === mark) allReadMarkRef.current = null;
      fetchNotifications();
    }
  }, [fetchNotifications]);

  const markRead = useCallback(async (id) => {
    const target = notificationsRef.current.find(n => n.id === id);
    if (target && target.is_read) return;
    const mark = { version: ++versionRef.current, settled: false };
    readMarksRef.current.set(id, mark);
    setNotifications(prev => prev.map(n => (n.id === id && !n.is_read ? { ...n, is_read: true } : n)));
    setServerUnread(prev => (typeof prev === 'number' && prev > 0 ? prev - 1 : prev));
    try {
      await APIAgent.request(`${OFFER_NOTIFICATIONS_URL}/${id}/read`, 'POST');
      mark.settled = true;
    } catch (err) {
      if (readMarksRef.current.get(id) === mark) readMarksRef.current.delete(id);
      fetchNotifications();
    }
  }, [fetchNotifications]);

  const unreadCount = useMemo(() => {
    // The server counts every unread row, not just the loaded page.
    if (typeof serverUnread === 'number') return serverUnread;
    return notifications.filter(n => !n.is_read).length;
  }, [notifications, serverUnread]);

  const groupedByDay = useMemo(() => {
    const groups = { Today: [], Yesterday: [], 'This Week': [], Older: [] };
    for (const n of notifications) {
      const key = getNotifDateGroup(n.created_at || n.sent_at || n.timestamp);
      if (groups[key]) groups[key].push(n);
    }
    return groups;
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    groupedByDay,
    loading,
    error,
    refetch: fetchNotifications,
    markAllRead,
    markRead,
  };
}
